import { useMemo, type ReactNode } from 'react'
import { motion } from 'framer-motion'
import { ENERGIE, MODULES, t } from '../content/content'
import { useKiosk } from '../state/store'
import { BEREIK, stap, vergelijk } from '../energie/rekenmotor'
import type { EnergieInvoer, EnergieUitkomst } from '../energie/rekenmotor'
import { VEER } from './gedeeld'

const VELDEN: (keyof EnergieInvoer)[] = ['trucks', 'shifts', 'uren', 'laadtijd']

/**
 * Energie: calculator → instellingen aanpassen → resultaat, lithium-ion naast
 * loodzuur (flowchart 5 oktober). Vier instellingen als tegels met stapknoppen
 * in de actiebalk — geen sliders (§5.4) — en het resultaat live in het podium.
 */
export function Energie({ terug }: { terug: ReactNode }) {
  const taal = useKiosk((s) => s.taal)
  const invoer = useKiosk((s) => s.energie)
  const zet = useKiosk((s) => s.zetEnergie)
  const module = MODULES.find((m) => m.id === 'energie')!

  const uitkomst = useMemo(() => vergelijk(invoer), [invoer])
  // Locale uit de taal, nooit hardgecodeerd (§4.8.2, §5.4).
  const locale = taal === 'nl' ? 'nl-NL' : 'en-GB'
  const getal = useMemo(() => new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }), [locale])

  const eenheid = (veld: keyof EnergieInvoer) =>
    veld === 'uren' || veld === 'laadtijd' ? t(ENERGIE.uurPerDag, taal) : veld === 'shifts' ? t(ENERGIE.perDag, taal) : ''

  return (
    <>
      <div className="kiosk-podium kiosk-podium-breed">
        <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={VEER}>
          <div className="kiosk-tegel-nr">{module.nr}</div>
          <h2 className="kiosk-hero kiosk-hero-s">{t(ENERGIE.kop, taal)}</h2>
          <p className="kiosk-onderkop">{t(ENERGIE.intro, taal)}</p>
        </motion.div>

        <motion.div
          className="kiosk-energie"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...VEER, delay: 0.2 }}
        >
          <Kolom titel={t(ENERGIE.lithium, taal)} u={uitkomst.lithium} getal={getal} taal={taal} aan />
          <Kolom titel={t(ENERGIE.loodzuur, taal)} u={uitkomst.loodzuur} getal={getal} taal={taal} />
        </motion.div>

        <motion.p
          className="kiosk-energie-besparing"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ ...VEER, delay: 0.35 }}
        >
          <b>{getal.format(uitkomst.besparingProcent)}%</b>{' '}
          {taal === 'nl' ? 'minder stroom uit het net met lithium-ion' : 'less grid power with lithium-ion'}
          <span className="kiosk-energie-aanname">{t(ENERGIE.aanname, taal)}</span>
        </motion.p>
      </div>

      <div className="kiosk-actiebalk">
        {terug}
        {VELDEN.map((veld) => (
          <div key={veld} className="kiosk-tegel kiosk-stapper">
            <span className="kiosk-tegel-sub">{t(ENERGIE.instellingen[veld], taal)}</span>
            <div className="kiosk-stapper-rij">
              <button
                className="kiosk-stap"
                aria-label="−"
                disabled={invoer[veld] <= BEREIK[veld].min}
                onClick={() => zet(veld, stap(invoer, veld, -1))}
              >
                −
              </button>
              <span className="kiosk-stapper-waarde">
                {getal.format(invoer[veld])}
                <small>{eenheid(veld)}</small>
              </span>
              <button
                className="kiosk-stap"
                aria-label="+"
                disabled={invoer[veld] >= BEREIK[veld].max}
                onClick={() => zet(veld, stap(invoer, veld, 1))}
              >
                +
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  )
}

function Kolom({ titel, u, getal, taal, aan = false }: {
  titel: string; u: EnergieUitkomst; getal: Intl.NumberFormat; taal: 'nl' | 'en'; aan?: boolean
}) {
  return (
    <div className={`kiosk-energie-kolom${aan ? ' kiosk-energie-aan' : ''}`}>
      <h3>{titel}</h3>
      <dl>
        <dt>{t(ENERGIE.uitkomst.batterijen, taal)}</dt>
        <dd>{getal.format(u.batterijenPerTruck)} <small>{taal === 'nl' ? 'per truck' : 'per truck'}</small></dd>
        <dt>{t(ENERGIE.uitkomst.wissels, taal)}</dt>
        <dd>{getal.format(u.wisselsPerDag)}</dd>
        <dt>{t(ENERGIE.uitkomst.energie, taal)}</dt>
        <dd>{getal.format(u.kWhPerDag)} <small>kWh</small></dd>
        <dt>{t(ENERGIE.uitkomst.laadvermogen, taal)}</dt>
        <dd>{getal.format(u.piekKw)} <small>kW</small></dd>
      </dl>
      {!u.pastInLaadtijd && <p className="kiosk-energie-waarschuwing">{t(ENERGIE.pastNiet, taal)}</p>}
    </div>
  )
}
